// libs
import React, { memo, useEffect, useState, useMemo } from 'react';
import { SerializedStyles } from '@emotion/core';

// helpers
import { inPriceFormat } from 'shared/helpers/utils';

// styles
import {
  Amount,
  Container,
  Currency,
  Discount,
  Label,
  OriginalPrice,
  LineThroughPrice,
  DownArrow,
  UrgencyTag,
  UrgencyContainer,
  MRP,
} from './styles';

// constants
import {
  ALL_INCLUSIVE_TEXTS,
  URGENCY_ARROW_COLOR,
  URGENCY_ARROW_SIZE,
  URGENCY_ANIMATION_BUFFER,
} from 'shared/layouts/DetailsPage/constants';
import { DEFAULT_CURRENCY } from 'shared/constants';

// defs

interface UrgencyData {
  text: string | undefined;
  enabled: boolean;
  textColor: string;
}

interface IPriceWithDiscountProps {
  discount?: string | number;
  discountedPrice?: string | number;
  price?: string | number;
  hidePercentage?: boolean;
  cssProps?: SerializedStyles | SerializedStyles[];
  className?: string;
  amountClassName?: string;
  originalAmountClassName?: string;
  inclusiveTextFull?: boolean;
  isStickyPrice?: boolean;
  isOutOfStock?: boolean;
  urgencyData?: UrgencyData;
  PriceChildren?: React.ReactNode;
}

function PriceWithDiscount({
  discount = 0,
  discountedPrice,
  price,
  hidePercentage = false,
  cssProps,
  className = '',
  amountClassName = '',
  originalAmountClassName = '',
  inclusiveTextFull = true,
  isStickyPrice = false,
  isOutOfStock = false,
  urgencyData,
  PriceChildren,
}: IPriceWithDiscountProps) {
  price = inPriceFormat(price);
  const [priceAnimation, setPriceAnimation] = useState(true);
  const urgencyNudgeEnabled =
    urgencyData?.enabled && isStickyPrice && urgencyData?.text?.length > 0 && !isOutOfStock;
  const showNudgeAnimation = PriceChildren || urgencyNudgeEnabled;
  const nudgeData = urgencyNudgeEnabled ? urgencyData?.text : PriceChildren;

  useEffect(() => {
    let timer;
    if (!priceAnimation) {
      timer = setTimeout(() => {
        setPriceAnimation(true);
      }, URGENCY_ANIMATION_BUFFER);
    }
    () => {
      clearTimeout(timer);
    };
  }, [priceAnimation]);

  const urgencytag = useMemo(() => {
    return (
      <UrgencyTag
        color={urgencyData?.textColor}
        priceAnimation={priceAnimation}
        onAnimationEnd={() => setPriceAnimation(false)}
      >
        {nudgeData}
      </UrgencyTag>
    );
  }, [nudgeData, priceAnimation, urgencyData?.textColor]);

  const labelWithDiscount = useMemo(() => {
    return (
      <Label className="mrp-label">
        <MRP>{ALL_INCLUSIVE_TEXTS.MRP}</MRP>
        <OriginalPrice className={originalAmountClassName} data-at="mrp-pdp">
          <Currency>{DEFAULT_CURRENCY}</Currency>
          <LineThroughPrice>{price}</LineThroughPrice>
        </OriginalPrice>
        <span className="sub" data-test="all-inclusive">
          {inclusiveTextFull ? ALL_INCLUSIVE_TEXTS.INCLUSIVE : ALL_INCLUSIVE_TEXTS.INCLUSIVE_HALF}
        </span>
      </Label>
    );
  }, [inclusiveTextFull, originalAmountClassName, price]);

  const labelWithoutDiscount = useMemo(() => {
    return (
      <Label className="price-label">
        <MRP>{ALL_INCLUSIVE_TEXTS.MRP}</MRP>
        <span className="sub" data-test="all-inclusive">
          {inclusiveTextFull ? ALL_INCLUSIVE_TEXTS.INCLUSIVE : ALL_INCLUSIVE_TEXTS.INCLUSIVE_HALF}
        </span>
      </Label>
    );
  }, [inclusiveTextFull]);

  return discount > 0 ? (
    <Container className={className} cssProps={cssProps} data-test="has-discount">
      <div>
        <Amount className={amountClassName} data-at="sp-pdp">
          <Currency>{DEFAULT_CURRENCY}</Currency>
          {inPriceFormat(discountedPrice)}
        </Amount>
        {urgencyNudgeEnabled && <DownArrow size={URGENCY_ARROW_SIZE} color={URGENCY_ARROW_COLOR} />}
        {hidePercentage === false && (
          <Discount
            data-test="discount-percentage"
            className="discountPercentage"
            data-at="price-offer"
          >
            {discount}% Off
          </Discount>
        )}
      </div>
      {priceAnimation && showNudgeAnimation ? (
        <UrgencyContainer className="urgency-nudge">
          {urgencytag}
          {labelWithDiscount}
        </UrgencyContainer>
      ) : (
        <>{labelWithDiscount}</>
      )}
    </Container>
  ) : (
    <Container className={className} cssProps={cssProps} data-test="no-discount">
      <Amount>
        <Currency>{DEFAULT_CURRENCY}</Currency>
        {price}
        {urgencyNudgeEnabled && <DownArrow size={URGENCY_ARROW_SIZE} color={URGENCY_ARROW_COLOR} />}
      </Amount>

      {priceAnimation && showNudgeAnimation ? (
        <UrgencyContainer className="urgency-nudge">
          {urgencytag}
          {labelWithoutDiscount}
        </UrgencyContainer>
      ) : (
        <>{labelWithoutDiscount}</>
      )}
    </Container>
  );
}

export default memo(PriceWithDiscount);
